import React, {useState} from 'react';
import {Accordion} from '../../Accordion/Accordion';
import {CheckboxStyled, ContentWrapper} from './FileMetaDataTabPermissions.style';
import {t} from 'i18next';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {AdvancedRolesPicker} from '../../RolePicker/AdvancedRolesPicker';
import EventEmitter from 'events';
import {
    FileMetaDataDialog_changeOwnerRoles,
    FileMetaDataDialog_changePublicAccess,
    FileMetaDataDialog_changeRoles
} from '../../../init/events';
import {getOwnerRoles, getUserRoles} from '../../../utils/UserUtils';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {isMandatoryComponent, isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_OBJECT_IS_PUBLIC,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_ROLES
} from '../../../init/storage_object_constants';
import {isValidBoolean} from '../../../utils';
import {Valid} from '../../validation/Valid';

export interface FileMetaDataTabPermissionsProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly isPublic: boolean;
    readonly roles?: string[];
    readonly ownerRoles?: string[];
    readonly rolesDataSource: string[];
    readonly parentOwnerRoles: string[];
    readonly parentRoles: string[];
    readonly eventEmitter: EventEmitter;
}

/**
 * @name FileMetaDataTabPermissions
 * @description
 * The dialog to add the metadata of a newly created file or file - Metadata Tabs
 */
export const FileMetaDataTabPermissions: React.VFC<FileMetaDataTabPermissionsProps> = (props) => {
    const [isPublicInput, setIsPublicInput] = useState<boolean>(props.isPublic ?? false);
    const [rolesInput, setRolesInput] = useState<string[]>(getUserRoles(props.roles));
    const [ownerRolesInput, setOwnerRolesInput] = useState<string[]>(getOwnerRoles(props.ownerRoles));
    const [rolesDataSource] = useState<string[]>(getUserRoles(props.rolesDataSource));
    const [ownerRolesDataSource] = useState<string[]>(getOwnerRoles(props.rolesDataSource));
    const [parentOwnerRolesInput] = useState(getOwnerRoles(props.parentOwnerRoles));
    const [parentRolesInput] = useState(getUserRoles(props.parentRoles));

    const handleIsPublic = () => {
        setIsPublicInput(!isPublicInput);
        props.eventEmitter.emit(FileMetaDataDialog_changePublicAccess, (!isPublicInput));
    };

    const handleRoles = (userRoles: string[]) => {
        setRolesInput(userRoles);
        props.eventEmitter.emit(FileMetaDataDialog_changeRoles, userRoles);
    };
    const handleOwnerRoles = (ownerRoles: string[]) => {
        setOwnerRolesInput(ownerRoles);
        props.eventEmitter.emit(FileMetaDataDialog_changeOwnerRoles, ownerRoles);
    };

    return (
        <ContentWrapper id={generateIdentifier()}>
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, props.templatePropertySettings)
                    || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, props.templatePropertySettings)))) &&
                <Accordion accordionHeadline={t('CREATE_EDIT_FOLDER_DIALOG_ACCORDION_TITLE_ROLES')}
                           startOpen>
                    {isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, props.templatePropertySettings) &&
                        <Valid
                            isValid={(isMandatoryComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, props.templatePropertySettings) ? isValidBoolean(isPublicInput) : true)}
                            message={t('ERROR_IS_PUBLIC_MISSING')}>
                            <CheckboxStyled id={generateIdentifier('IsPublic')}
                                            key={'IsPublic'}
                                            label={t('PUBLIC_ACCESS')}
                                            checked={isPublicInput}
                                            onChange={handleIsPublic}/>
                        </Valid>
                    }
                    {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                            && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES, props.templatePropertySettings)
                                || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, props.templatePropertySettings))) &&
                        <AdvancedRolesPicker templatePropertySettings={props.templatePropertySettings}
                                             isOwnerModeEnabled={false}
                                             dataSource={rolesDataSource}
                                             roles={rolesInput}
                                             parentRoles={parentRolesInput}
                                             onChange={handleRoles}/>
                    }
                </Accordion>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES, props.templatePropertySettings)
                        || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES, props.templatePropertySettings))) &&
                <Accordion accordionHeadline={t('CREATE_EDIT_FOLDER_DIALOG_ACCORDION_TITLE_OWNER_ROLES')}
                           startOpen>
                    <AdvancedRolesPicker templatePropertySettings={props.templatePropertySettings}
                                         isOwnerModeEnabled={true}
                                         dataSource={ownerRolesDataSource}
                                         roles={ownerRolesInput}
                                         parentRoles={parentOwnerRolesInput}
                                         onChange={handleOwnerRoles}/>
                </Accordion>
            }
        </ContentWrapper>
    );
}
